import React from 'react';

import type { BuckChatConfig, CurrentUser, LtPlusCta, StripeConfig } from '../../types';
import type { AnalyticsHandler } from '../SignupDrawer/hooks/useSignupFlow';
import BenefitsStep from './components/BenefitsStep';
import CheckoutStep from './components/CheckoutStep';
import ConfirmationStep from './components/ConfirmationStep';
import { useLtPlusFlow } from './hooks/useLtPlusFlow';
import { VARIANT_CONTENT } from './LtPlusDrawer.constants';

type LtPlusDrawerProps = {
  ltPlusCta: LtPlusCta | null;
  stripeConfig: StripeConfig;
  /**
   * New multi-step flow props
   */
  buckChatConfig?: BuckChatConfig;
  currentUser?: CurrentUser;
  initialStep?: 'benefits' | 'checkout';
  onClose?: () => void;
  onSignInRequired?: () => void;
  /**
   * Legacy props kept temporarily for backward compatibility
   */
  onCheckoutClick?: () => void;
  onSignInClick?: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

const LtPlusDrawer: React.FC<LtPlusDrawerProps> = ({
  ltPlusCta,
  stripeConfig,
  buckChatConfig,
  currentUser,
  initialStep,
  onClose,
  onSignInRequired,
  onSignInClick,
  onAnalyticsEvent,
}) => {
  if (!ltPlusCta) {
    return null;
  }

  if (!buckChatConfig || !onClose) {
    return null;
  }

  const {
    step,
    showSignInButton,
    selectedBillingInterval,
    setSelectedBillingInterval,
    navigateToCheckout,
    navigateBackToBenefits,
    handlePaymentSuccess,
    handleDone,
  } = useLtPlusFlow({
    initialUser: currentUser ?? {},
    initialStep,
    onComplete: onClose,
    onAnalyticsEvent,
  });

  const variant = ltPlusCta.variant || 'general';
  const confirmationBenefits = (VARIANT_CONTENT[variant] || VARIANT_CONTENT.general).benefits;

  if (step === 'benefits') {
    const handleBenefitsPrimaryAction = showSignInButton
      ? (onSignInRequired ?? onSignInClick ?? (() => undefined))
      : navigateToCheckout;

    return (
      <BenefitsStep
        ltPlusCta={ltPlusCta}
        stripeConfig={stripeConfig}
        showSignInButton={showSignInButton}
        selectedBillingInterval={selectedBillingInterval}
        onBillingIntervalChange={setSelectedBillingInterval}
        onCheckoutClick={handleBenefitsPrimaryAction}
        onSignInClick={onSignInRequired ?? onSignInClick ?? (() => undefined)}
        onAnalyticsEvent={onAnalyticsEvent}
      />
    );
  }

  if (step === 'checkout') {
    return (
      <CheckoutStep
        stripeConfig={stripeConfig}
        buckChatConfig={buckChatConfig}
        billingInterval={selectedBillingInterval}
        onSuccess={handlePaymentSuccess}
        onBack={navigateBackToBenefits}
        onAnalyticsEvent={onAnalyticsEvent}
      />
    );
  }

  return (
    <ConfirmationStep
      benefits={confirmationBenefits}
      onDone={handleDone}
      onAnalyticsEvent={onAnalyticsEvent}
    />
  );
};

export default LtPlusDrawer;
